import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  Minimize2,
  RotateCcw,
  User,
  Sparkles,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PoloChatbot({ user }) {
  const [isVisible, setIsVisible] = useState(false); // Controls whether chatbot UI is visible at all
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  // Add welcome message when chat first opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          role: "assistant",
          content: "Hi! I'm Polo, your SCC Football assistant. 🐴\n\nI can help you with:\n• **Logging in** or resetting your password\n• **Signing up** for games\n• **Captain guides** - creating games, squad selection, charge sheets\n• **Calendar sync** and availability\n• **Notifications** setup\n\nWhat would you like help with?",
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: "user",
      content: inputValue.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API}/chatbot/chat`,
        {
          message: userMessage.content,
          session_id: sessionId,
        },
        { withCredentials: true }
      );

      // Update session ID if new
      if (response.data.session_id && !sessionId) {
        setSessionId(response.data.session_id);
      }

      const assistantMessage = {
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Chatbot error:", error);
      const errorMessage = {
        role: "assistant",
        content: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment, or contact admin@sccfootball.com for immediate assistance.",
        timestamp: new Date().toISOString(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = () => {
    setMessages([
      {
        role: "assistant",
        content: "Hi! I'm Polo, starting a fresh conversation. How can I help you today?",
        timestamp: new Date().toISOString(),
      },
    ]);
    setSessionId(null);
  };

  const toggleChat = () => {
    if (isMinimized) {
      setIsMinimized(false);
    } else {
      setIsOpen(!isOpen);
    }
  };

  // Activate button - shows small icon to reveal the chatbot
  const activateChatbot = () => {
    setIsVisible(true);
    setIsOpen(true);
  };

  // Hide chatbot completely
  const hideChatbot = () => {
    setIsVisible(false);
    setIsOpen(false);
    setIsMinimized(false);
  };

  // When not visible, show only a small, subtle activation button
  if (!isVisible) {
    return (
      <button
        onClick={activateChatbot}
        className="fixed bottom-4 right-4 z-50 w-12 h-12 bg-slate-800/80 hover:bg-gradient-to-r hover:from-amber-500 hover:to-orange-500 text-slate-400 hover:text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center group border border-slate-700 hover:border-transparent"
        data-testid="polo-activate-button"
        title="Open Polo Assistant"
      >
        <MessageCircle className="w-5 h-5" />
      </button>
    );
  }

  // Floating button when closed (but visible/activated)
  if (!isOpen) {
    return (
      <button
        onClick={toggleChat}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 group"
        data-testid="polo-chat-button"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="font-medium">Ask Polo</span>
        <Sparkles className="w-4 h-4 opacity-75 group-hover:opacity-100" />
      </button>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all animate-pulse"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="font-medium">Polo</span>
        {messages.length > 1 && (
          <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
            {messages.length}
          </span>
        )}
      </button>
    );
  }

  return (
    <Card
      className="fixed bottom-4 right-4 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100vh-8rem)] flex flex-col bg-slate-900 border-slate-700 shadow-2xl overflow-hidden"
      data-testid="polo-chat-window"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <MessageCircle className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-sm">Polo</h3>
            <p className="text-xs text-white/80">SCC Football Assistant</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={startNewChat}
            className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/20"
            title="New conversation"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMinimized(true)}
            className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/20"
            title="Minimize"
          >
            <Minimize2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={hideChatbot}
            className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/20"
            title="Hide Polo"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                msg.role === "user"
                  ? "bg-amber-500/100 text-white rounded-br-md"
                  : msg.isError
                  ? "bg-red-500/100/20 text-red-300 border border-red-500/30 rounded-bl-md"
                  : "bg-slate-800 text-slate-200 rounded-bl-md"
              }`}
            >
              <div className="flex items-start gap-2">
                {msg.role === "assistant" && (
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles className="w-3 h-3 text-white" />
                  </div>
                )}
                <div className="text-sm whitespace-pre-wrap" dangerouslySetInnerHTML={{ 
                  __html: msg.content
                    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
                    .replace(/\n• /g, '<br/>• ')
                    .replace(/\n- /g, '<br/>- ')
                    .replace(/\n\d\. /g, (match) => '<br/>' + match.trim() + ' ')
                }} />
                {msg.role === "user" && (
                  <User className="w-4 h-4 text-white/70 flex-shrink-0 mt-0.5" />
                )}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-slate-800 text-slate-200 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                  <Sparkles className="w-3 h-3 text-white animate-pulse" />
                </div>
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Action Suggestions - shown after welcome message */}
        {messages.length === 1 && !isLoading && (
          <div className="flex flex-wrap gap-2 px-2">
            {[
              "How do I sign in?",
              "How do I join a game?",
              "How to create a game",
              "Reset my password",
              "Connect my calendar"
            ].map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setInputValue(suggestion);
                  // Auto-send after small delay
                  setTimeout(() => {
                    const btn = document.querySelector('[data-testid="polo-send-button"]');
                    if (btn) btn.click();
                  }, 100);
                }}
                className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded-full border border-slate-700 hover:border-amber-500/50 transition-all"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-slate-700 bg-slate-900/80">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask Polo anything..."
            className="flex-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-400 focus:border-amber-500"
            disabled={isLoading}
            data-testid="polo-chat-input"
          />
          <Button
            onClick={sendMessage}
            disabled={!inputValue.trim() || isLoading}
            className="bg-amber-500/100 hover:bg-amber-600 text-white px-3"
            data-testid="polo-send-button"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-2 text-center">
          Polo is AI-powered. For urgent matters, contact admin@sccfootball.com
        </p>
      </div>
    </Card>
  );
}
